import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { isDefined } from '../../utilities/is-defined';
import type { ODataLongText } from '../odata-types/multiline-text.type';
import type {
    ODataNotificationCode,
    ODataNotificationContract,
    ODataNotificationDop,
    ODataNotificationEmail,
    ODataNotificationEquipment,
    ODataNotificationHistory,
    ODataNotificationPaperwork,
    ODataNotificationPart,
    ODataNotificationParticipant,
    ODataNotificationTask,
} from '../odata-types/notification.type';
import type {
    NotificationCodeState,
    NotificationContractRowState,
    NotificationDropOffPointState,
    NotificationEmailState,
    NotificationEquipmentState,
    NotificationHistoryEntryState,
    NotificationLongTextLineState,
    NotificationPaperworkQuestionState,
    NotificationParticipantState,
    NotificationPartState,
    NotificationTaskState,
} from './notification-entities.state';

/**
 * The conversion of the notification's sub-objects: one `NdpHeaderSet` row in, one row of the app's
 * own type out (see `notification-entities.state.ts`). Called by `odataToNotificationState` for each
 * expanded set, and the only place that reads the OData field names of those rows.
 *
 * Pure, like the header conversion it belongs to: no config, no clock, no injector. A field the
 * backend left empty converts to the empty value for its type rather than to a hole the readers would
 * have to test for, and the one row that is not converted at all is the long text line with no text —
 * see {@link odataToNotificationLongTextLines}.
 */

/** A SAP OData V2 date literal, e.g. `/Date(1523250000000)/` or `/Date(1523250000000+0120)/`. */
const SAP_DATE_PATTERN = /^\/Date\((-?\d+)([+-]\d{4})?\)\/$/;

/**
 * A numeric SAP field as an integer, or `undefined` when the backend sent nothing for it. Accepts a
 * number as well as a string: several `Counter` fields are declared as strings by the service
 * metadata but arrive as numbers.
 *
 * @param value the raw field
 */
export function parseIntegerField(value: string | number | undefined): number | undefined {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? Math.trunc(value) : undefined;
    }
    if (!value) {
        return undefined;
    }
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * A numeric SAP field as a decimal, for the amounts and prices the backend sends with three decimal
 * places (`'2098.540'`). `undefined` when it sent nothing.
 *
 * @param value the raw field
 */
export function parseDecimalField(value: string | number | undefined): number | undefined {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }
    if (!value) {
        return undefined;
    }
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * A row's `Counter`, i.e. its position within its entity set. Falls back to `0` rather than to
 * `undefined`: the backend sends a counter on every row, and a total order is what the readers that
 * sort by it need — see `sortedByPosition`.
 *
 * @param value the raw `Counter` field
 */
function parsePosition(value: string | number | undefined): number {
    return parseIntegerField(value) ?? 0;
}

/**
 * A standalone SAP date as epoch milliseconds, or `undefined` when the backend sent none — which it
 * does as `null`, so a missing date is the normal case rather than an error. See the doc comment on
 * `notification-entities.state.ts` for why a *paired* date and time stay strings instead.
 *
 * @param value the raw date field, an OData V2 date literal or an ISO 8601 date
 */
export function parseSapDate(value: string | undefined): number | undefined {
    if (!value) {
        return undefined;
    }
    const sapDate = SAP_DATE_PATTERN.exec(value);
    if (sapDate) {
        // A literal carrying a UTC offset means a local datetime, which only moment resolves correctly.
        return sapDate[2] ? moment(value).valueOf() : Number(sapDate[1]);
    }
    const parsed = moment(value, moment.ISO_8601, true);
    return parsed.isValid() ? parsed.valueOf() : undefined;
}

/**
 * An SAP boolean field as a boolean: set when it carries the backend's "true" character.
 *
 * @param value the raw field
 * @param trueValue the character that means `true`, `'X'` by default
 */
export function parseSapFlag(value: string | undefined, trueValue: string = ConfigConstants.sapTrue): boolean {
    return value === trueValue;
}

/**
 * A text field as the string the row's type promises. The payload's own type declares every text
 * field as a `string`, but the backend leaves a field it has nothing for out of the row altogether,
 * so without this the slice would hold `undefined` where it says `string` — the readers of these
 * rows are the ones that would pay for that.
 *
 * @param value the raw field
 */
function textField(value: string | undefined): string {
    return value ?? '';
}

/**
 * One `CodeSet` row.
 *
 * @param code the row as the backend sent it
 */
export function odataToNotificationCode(code: ODataNotificationCode): NotificationCodeState {
    return {
        position: parsePosition(code.Counter),
        codeType: textField(code.CodeType),
        catalogType: textField(code.Qkatart),
        codeGroup: textField(code.CodeGroup),
        codeName: textField(code.CodeName),
        codeDescription: textField(code.CodeDesc),
    };
}

/**
 * One `ContractSet` row.
 *
 * @param contract the row as the backend sent it
 */
export function odataToNotificationContractRow(contract: ODataNotificationContract): NotificationContractRowState {
    return {
        position: parsePosition(contract.Counter),
        label: textField(contract.ContLabel),
        value: textField(contract.ContValue),
    };
}

/**
 * One `DopSet` row.
 *
 * @param dop the row as the backend sent it
 */
export function odataToNotificationDropOffPoint(dop: ODataNotificationDop): NotificationDropOffPointState {
    return {
        position: parsePosition(dop.Counter),
        purchaseOrderNumber: parseIntegerField(dop.PONum),
        dropOffPoint: textField(dop.DropOfPoint),
        name1: textField(dop.Name1),
        name2: textField(dop.Name2),
        department: textField(dop.Department),
        room: textField(dop.Room),
        city: textField(dop.City),
        postalCode: textField(dop.PostalCode),
        street: textField(dop.Street),
        additionalInfo: textField(dop.AddInfo),
        building: textField(dop.Building),
        floor: textField(dop.Floor),
        state: textField(dop.State),
        phone: textField(dop.Telephone),
        addressNumber: textField(dop.AddrNumber),
    };
}

/**
 * One `EmailSet` row.
 *
 * @param email the row as the backend sent it
 */
export function odataToNotificationEmail(email: ODataNotificationEmail): NotificationEmailState {
    return {
        position: parsePosition(email.Counter),
        faxOrEmail: textField(email.FaxEmail),
        communicationCategory: textField(email.CommCategory),
    };
}

/**
 * One `EquipmentsSet` row.
 *
 * @param equipment the row as the backend sent it
 */
export function odataToNotificationEquipment(equipment: ODataNotificationEquipment): NotificationEquipmentState {
    return {
        position: parsePosition(equipment.Counter),
        equipmentNumber: textField(equipment.EquipmentNum),
        technicalIdentificationNumber: textField(equipment.TechIdentNum),
        mainEquipmentIndicator: textField(equipment.MEIndicator),
        manufacturerPartNumber: textField(equipment.ManPartNum),
        materialNumber: textField(equipment.MaterialNum),
        serialNumber: textField(equipment.SerialNum),
        warrantyEndDate: parseSapDate(equipment.WarrantyEndDate),
        division: textField(equipment.Division),
        shortDescription: textField(equipment.EquiShortDesc),
        contractType: textField(equipment.ContractType),
        mk: textField(equipment.Mk),
        tk: textField(equipment.Tk),
        mkV2: textField(equipment.MkV2),
        measuringPoint: textField(equipment.MeasuringPoint),
        measuringPointDescription: textField(equipment.MeasuringPointDesc),
        internalCharacteristic: textField(equipment.IntCharacteristic),
        externalUnit: textField(equipment.ExtUnit),
        mandatory: textField(equipment.Mandatory),
        serviceKey: textField(equipment.ServiceKey),
        barcodeMaterial: textField(equipment.BarcodeMaterial),
        barcodeSerialNumber: textField(equipment.BarcodeSernr),
        ipAddress: textField(equipment.IpAddress),
        hostId: textField(equipment.HostId),
        dongle: textField(equipment.Dongle),
        macAddress: textField(equipment.MacAdresse),
        softwareVersion: textField(equipment.SoftwareVers),
        endOfDeliveryDate: parseSapDate(equipment.EndOfDeliveryDate),
        endOfSupportDate: parseSapDate(equipment.EndOfSupportDate),
    };
}

/**
 * One `HistorySet` row.
 *
 * @param history the row as the backend sent it
 */
export function odataToNotificationHistoryEntry(history: ODataNotificationHistory): NotificationHistoryEntryState {
    return {
        position: parsePosition(history.Counter),
        notificationNumber: textField(history.NotificationNo),
        taskNumber: textField(history.TaskNumber),
        shortText: textField(history.ShortText),
        notificationType: textField(history.NotificationType) as ConfigConstants.NotificationTypes,
        createdDate: textField(history.CreatedDate),
        createdTime: textField(history.CreatedOn),
        cseName: textField(history.CSEName),
        outstanding: parseIntegerField(history.Outstanding),
    };
}

/**
 * One `PartSet` row.
 *
 * @param part the row as the backend sent it
 */
export function odataToNotificationPart(part: ODataNotificationPart): NotificationPartState {
    return {
        position: parsePosition(part.Counter),
        purchaseOrderNumber: textField(part.PONumber),
        previousPurchaseOrderNumber: textField(part.PONumberOld),
        previousOrder: parseIntegerField(part.PreviousOrder),
        itemNumber: textField(part.ItemNumber),
        materialNumber: textField(part.Material),
        description: textField(part.DescText),
        quantity: parseIntegerField(part.Quantity),
        amount: parseDecimalField(part.Amount),
        cost: parseDecimalField(part.Cost),
        repairPrice: parseDecimalField(part.RepPrice),
        customerPrice: parseDecimalField(part.CustomerPrice),
        currency: textField(part.Currency),
        customerCurrency: textField(part.CustomerCurrency),
        materialCharacteristic: textField(part.MatMerkmal),
        externalUnit: textField(part.ExtUnit),
        airWaybillNumber: textField(part.Awbnr),
        batchNumber: textField(part.BatchNumber),
        batchNumberLegacy: textField(part.BatchNumberLegacy),
        sparePartText: textField(part.SparepartTxt),
        strahler: textField(part.Strahler),
        returnDeadline: parseSapDate(part.RFrist),
        xRayDateOld: parseSapDate(part.XRayDateOld),
        endOfDeliveryDate: parseSapDate(part.EodDate),
        endOfSupportDate: parseSapDate(part.EosDate),
        tubeIndicator: textField(part.TubeIndicator),
        radioactiveIndicator: textField(part.RadioactIndicat),
        serialPartIndicator: textField(part.SerialPIndicat),
        localScrapReturnC: textField(part.LocalScrapReturnC),
        localScrapReturnD: textField(part.LocalScrapReturnD),
        localScrapReturnV: textField(part.LocalScrapReturnV),
        localScrapReturnX: textField(part.LocalScrapReturnX),
        localScrapReturnChangeable: textField(part.LsCrChangeable),
        ucrNumber: textField(part.UCRNumber),
        unNumber: textField(part.UNNumber),
        dangerousGoodsIndicator: textField(part.DangerousGoodsIndicator),
        hazardousMaterial: textField(part.HazardousMaterial),
        dangerousGoodsText: textField(part.PROFText),
        hazardousMaterialText: textField(part.STOFText),
        // A part the backend sends no billing code for is the automatic case, which is `''`.
        billing: (part.Billing ?? ConfigConstants.BILLABLE_CODE.AUTO) as ConfigConstants.BILLABLE_CODE,
        // The backend spells out only the "not changeable" case, so anything else means changeable.
        billingChangeable: part.BillingChangeable !== ConfigConstants.MATERIAL_BILLABLE_CHANGEABLE.No,
        serialNumberOld: textField(part.SerialNumberOld),
        serialNumberNew: textField(part.SerialNumberNew),
        textLine: textField(part.TextLine),
        repairable: parseSapFlag(part.Repairable, ConfigConstants.materialRepairableIndicator),
        qualityPart: textField(part.QualityPart),
        spqDocumentId: textField(part.SPQDocID),
        fda2579Enabled: parseSapFlag(part.EnableFDA2579),
        medPhysicsRelevant: parseSapFlag(part.MedPhysicsFlag),
    };
}

/**
 * One `ParticipantSet` row.
 *
 * @param participant the row as the backend sent it
 */
export function odataToNotificationParticipant(
    participant: ODataNotificationParticipant,
): NotificationParticipantState {
    return {
        position: parsePosition(participant.Counter),
        title: textField(participant.Title),
        firstName: textField(participant.FirstName),
        lastName: textField(participant.LastName),
        email: textField(participant.Email),
        mobileNumber: textField(participant.MobileNumber),
        trainingDate: parseSapDate(participant.TRDate),
        trainingName: textField(participant.TrainingName),
        shared: textField(participant.Shared),
        trainerFirstName: textField(participant.TRFname),
        trainerLastName: textField(participant.TRLname),
        duration: textField(participant.Duration),
        productName: textField(participant.ProductName),
        creditFlag: textField(participant.CreditFlag),
    };
}

/**
 * One `TaskSet` row.
 *
 * @param task the row as the backend sent it
 */
export function odataToNotificationTask(task: ODataNotificationTask): NotificationTaskState {
    return {
        position: parsePosition(task.Counter),
        taskNumber: textField(task.TaskNumber),
        statusCode: textField(task.TaskCode) as NOTIFICATION_STATUS_CODE,
        plannedStartDate: textField(task.PlannedStartDate),
        plannedStartTime: textField(task.PlannedStartTime),
        plannedEndDate: textField(task.PlannedFinishDate),
        plannedEndTime: textField(task.PlannedFinishTime),
    };
}

/**
 * One `PaperWorkSet` row.
 *
 * @param question the row as the backend sent it
 */
export function odataToNotificationPaperworkQuestion(
    question: ODataNotificationPaperwork,
): NotificationPaperworkQuestionState {
    return {
        sapId: textField(question.SapId),
        required: question.Required ?? false,
        responseOptions: textField(question.ResponseOptions),
        filePrefix: textField(question.FilePrefix),
        alreadyUploaded: question.AlreadyUploaded ?? false,
        fileName: textField(question.FileName),
        previousResponse: textField(question.Previousresponse),
    };
}

/**
 * One line of a long text, from any of the `*LtextSet` sets.
 *
 * `NotificationNo` and `TaskNumber` are kept as sent rather than run through {@link textField}: for
 * most sets they just repeat the notification's own identifier, but `HistoryLtextSet` rows carry the
 * *previous* notification they were written on (see {@link NotificationLongTextLineState}), so an
 * absent number has to stay absent — `notificationLongTextLinesToOdata` falls back to the current
 * notification's identifier only when there is nothing here to keep.
 *
 * @param line the row as the backend sent it
 */
function odataToNotificationLongTextLine(line: ODataLongText): NotificationLongTextLineState {
    return {
        textId: textField(line.TextID),
        lineNumber: parsePosition(line.Counter),
        text: textField(line.TextLine),
        notificationNumber: line.NotificationNo || undefined,
        taskNumber: line.TaskNumber || undefined,
    };
}

/**
 * The lines of one long text. A line the backend sent without any text at all is dropped: the
 * declared `TextLine` is a string, but the payload can carry a `null` there, and every reader of
 * these lines went through `TextInputUtilities.getLongArray`, which skipped such a line. Dropping it
 * once here is what keeps `null` out of the slice — see the "never `null`" rule — without changing
 * what any reader sees.
 *
 * @param lines the rows of a `*LtextSet`, or `undefined` when the backend did not expand it
 */
export function odataToNotificationLongTextLines(
    lines: readonly ODataLongText[] | undefined,
): NotificationLongTextLineState[] {
    return (lines ?? []).filter((line) => isDefined(line.TextLine)).map(odataToNotificationLongTextLine);
}
